import React from 'react';
import type { Modify } from 'corgi-types';

import type { PrismicImage } from 'common/components/PrismicResponsiveImage';
import BannerTextBoxContent from '../../../banner/TextBoxContent';
import { ImageContainerType, PrismicSliceType } from '../../../enums';
import TeaserTextBoxContent from '../../../teaser/TextBoxContent';
import type { BannerProps, TeaserProps } from '../../../types';
import { checkMissingText } from '../../utils/text';
import DesktopTextBox from '../containers/DesktopTextBox';
import FloatingTextBox from '../containers/FloatingTextBox';
import GridConstraintOverlay from '../containers/GridConstraintOverlay';
import MobileTextBox from '../containers/MobileTextBox';
import LegalDisclaimer from '../content/LegalDisclaimer';
import MediaContainer from '../MediaContainer';
import ResponsiveImage from '../ResponsiveImage';

type SlimVariantProps = Modify<
  Omit<BannerProps | TeaserProps, 'slimImage' | 'bigImage' | 'tracking' | 'video'>,
  {
    image: PrismicImage;
    onClick?: () => void;
    onCtaClick?: () => void;
    onVoucherClick?: () => void;
    ref?: React.Ref<HTMLDivElement>;
  }
>;

const SlimVariant = ({ ref, type, image, isFullWidth, ...props }: SlimVariantProps) => {
  const isTextMissing = checkMissingText(props);

  const TextBoxContent =
    type === PrismicSliceType.TopTeaser ? TeaserTextBoxContent : BannerTextBoxContent;
  const TextBoxContainer = props?.isViewportWide ? GridConstraintOverlay : React.Fragment;

  return (
    <>
      <MediaContainer ref={ref} {...props}>
        <TextBoxContainer>
          {isFullWidth ? (
            <FloatingTextBox isTextMissing={isTextMissing} {...props}>
              <TextBoxContent {...props} isFloatingTextBox={true} />
            </FloatingTextBox>
          ) : (
            <DesktopTextBox isTextMissing={isTextMissing} {...props}>
              <TextBoxContent {...props} isFloatingTextBox={false} />
            </DesktopTextBox>
          )}
        </TextBoxContainer>
        <ResponsiveImage
          type={ImageContainerType.Slim}
          image={image}
          isFullWidth={isFullWidth}
          {...props}
        />
        <LegalDisclaimer
          legalDisclaimerContent={props?.legalDisclaimerContent}
          textContainerPosition={props?.textContainerPosition}
          isFullWidth={isFullWidth}
        />
      </MediaContainer>
      <MobileTextBox isTextMissing={isTextMissing} {...props}>
        <TextBoxContent {...props} isFloatingTextBox={false} />
      </MobileTextBox>
    </>
  );
};

SlimVariant.displayName = 'SlimVariant';

export default SlimVariant;
